/**
 * Zoom API 통합 서비스
 * Firebase Cloud Functions를 통해 Zoom 미팅을 관리합니다.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();

/**
 * Zoom 미팅 생성
 * @param {Object} meetingData - 미팅 정보
 * @param {string} meetingData.title - 미팅 제목
 * @param {string} meetingData.description - 미팅 설명
 * @param {string} meetingData.startTime - 시작 시간 (ISO 8601 format)
 * @param {number} meetingData.duration - 지속 시간 (분)
 * @returns {Promise<Object>} 생성된 미팅 정보
 */
export const createMeeting = async (meetingData) => {
  try {
    const createZoomMeeting = httpsCallable(functions, 'createZoomMeeting');
    const result = await createZoomMeeting({
      title: meetingData.title,
      description: meetingData.description,
      startTime: meetingData.startTime,
      duration: meetingData.duration
    });

    return result.data;
  } catch (error) {
    console.error('Create meeting error:', error);
    throw new Error(error.message || 'Zoom 미팅 생성에 실패했습니다.');
  }
};

/**
 * Zoom 미팅 정보 조회 (Cloud Function 미구현 - 필요시 추가)
 * @param {string} meetingId - 미팅 ID
 * @returns {Promise<Object>} 미팅 정보
 */
export const getMeetingDetails = async (meetingId) => {
  console.warn('getMeetingDetails: Cloud Function not implemented yet');
  return null;
};

/**
 * Zoom 미팅 참여자 정보 조회 (Cloud Function 미구현 - 필요시 추가)
 * @param {string} meetingId - 미팅 ID
 * @returns {Promise<Array>} 참여자 목록
 */
export const getMeetingParticipants = async (meetingId) => {
  console.warn('getMeetingParticipants: Cloud Function not implemented yet');
  return [];
};

/**
 * Zoom 미팅 녹화본 정보 조회
 * @param {string} meetingId - 미팅 ID
 * @returns {Promise<Object|null>} 녹화본 정보 또는 null
 */
export const getMeetingRecordings = async (meetingId) => {
  try {
    const getZoomRecordings = httpsCallable(functions, 'getZoomRecordings');
    const result = await getZoomRecordings({ meetingId });
    return result.data;
  } catch (error) {
    console.error('Get recordings error:', error);
    return null;
  }
};

/**
 * Zoom 미팅 삭제
 * @param {string} meetingId - 미팅 ID
 * @returns {Promise<void>}
 */
export const deleteMeeting = async (meetingId) => {
  try {
    const deleteZoomMeeting = httpsCallable(functions, 'deleteZoomMeeting');
    await deleteZoomMeeting({ meetingId });
  } catch (error) {
    console.error('Delete meeting error:', error);
    throw new Error(error.message || '미팅 삭제에 실패했습니다.');
  }
};

export const zoomService = {
  createMeeting,
  getMeetingDetails,
  getMeetingParticipants,
  getMeetingRecordings,
  deleteMeeting
};

export default zoomService;
